import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { AssetId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbFileText, TbX } from "solid-icons/tb";
import { Show, onCleanup, onMount } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";

export const VariableV1Asset = (props: {
  notInPromptBar?: boolean;
  id: string;
  selected?: boolean;
  onDelete: () => void;
  isEditable?: boolean;
  text?: string;
}) => {
  const wire = useWire();
  const asset = () => wire.assets.get(props.id as AssetId);

  // TODO: @andi, Better way to communicate between prompt bar and app state?
  onMount(() => {
    if (props.notInPromptBar) return;
    const unsubscribe = subscribeToGlobalEvent("removed-item-from-prompt-scope", (event) => {
      if (event.detail.id === props.id) {
        props.onDelete();
      }
    });
    dispatchGlobalEvent("added-mention-to-prompt-bar", {
      id: props.id,
    });
    onCleanup(() => {
      unsubscribe();
      dispatchGlobalEvent("removed-mention-from-prompt-bar", {
        id: props.id,
      });
    });
  });

  const isDeleted = () => asset()?.status.state === "DELETED";

  const truncatedFilename = () => {
    const maxLength = 40;
    const filename = asset()?.filename || props.text || "";
    if (filename.length <= maxLength) return isDeleted() ? `(Deleted) ${filename}` : filename;

    const extIndex = filename.lastIndexOf(".");
    const extension = extIndex !== -1 ? filename.slice(extIndex) : "";
    const nameWithoutExt = extIndex !== -1 ? filename.slice(0, extIndex) : filename;

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    if (charsToShow <= 0) return isDeleted() ? `(Deleted) ${filename}` : filename;

    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    const truncated = `${nameWithoutExt.slice(0, frontChars)}...${nameWithoutExt.slice(nameWithoutExt.length - backChars)}${extension}`;
    return isDeleted() ? `(Deleted) ${truncated}` : truncated;
  };

  const assetName = asset()?.filename || props.text || "Unknown file";

  return (
    <>
      <span class="inline-block w-1">&nbsp;</span>
      <span
        onClick={(e) => {
          // Don't navigate if clicking the delete button or if deleted
          if ((e.target as HTMLElement).closest("button") || isDeleted()) return;
          e.preventDefault();
          // Clear any selected concept first to ensure we show the assets table
          dispatchGlobalEvent("clear-selected-concept", undefined);
          wire.knowledgeUi.open();
          wire.knowledgeUi.setActiveTab("assets");
          dispatchGlobalEvent("open-asset-in-knowledge-pane", { assetId: props.id });
        }}
        class={twMerge(
          "group/mention inline-flex items-center gap-1 bg-asset-bg rounded-lg text-sm text-asset-text hover:bg-asset-bg-hover border px-1 border-asset-border",
          props.selected && "ring-2 ring-asset-border",
          isDeleted() && "line-through pointer-events-none",
          !isDeleted() && "cursor-pointer",
        )}
      >
        <TbFileText class="size-3.5 group-hover/mention:hidden" />
        <Show when={props.isEditable && !isDeleted()}>
          <StTooltip placement="top" contentProps={{ class: "z-20" }} content={<p>Remove</p>}>
            <StTooltipTrigger
              as="button"
              onClick={() => {
                props.onDelete();
              }}
              type="button"
              class="transition-transform text-asset-icon cursor-pointer hidden group-hover/mention:flex items-center gap-1"
            >
              <TbX class="size-3.5 text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
              <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
            </StTooltipTrigger>
          </StTooltip>
        </Show>
        <span class="hover:font-bold">{truncatedFilename()}</span>
      </span>
    </>
  );
};
